import { defineStore } from 'pinia'
import type { PayPalScriptOptions } from '@paypal/paypal-js'
import type {
  Invoice, InvoiceConfirmation,
  InvoiceSubscription,
  ShippingMethod,
  UpdatePaymentMethodPayload,
  UpdateRecurlyPaymentMethodPayload
} from '~/types/checkout'

import { useRootStore } from '~/stores/root'
import type { CheckoutProperties } from '~/types/persistent'
import { addCommonQueryParam } from '~/utils/general'
import type { Address } from '~/types/account'
import { PayPalFunding, DEFAULT_PAYPAL_SDK_NAMESPACE } from '~/types/checkout'
import { useFeaturesStore } from '~/stores/features'
import { useLocaleStore } from '~/stores/locale'
import { usePersistentStore } from '~/stores/persistent'
import { Env } from '~/types/environment'
import { type CountryCode, PAYLATER_SUPPORTED_COUNTRIES } from '~/types/locale'
import { getApiErrorData, getErrorStatus, toError } from '~/utils/http/api-error'
import { asRecord, asString } from '~/utils/narrow'

export const useCheckoutStore = defineStore('checkout', () => {
  const invoiceMailing = ref<Invoice>()
  const invoice = ref<Invoice>()
  const confirmationInvoice = ref<InvoiceConfirmation>()
  const invoiceSubscription = ref<InvoiceSubscription>()
  const currentCatalogMailingId = ref<string>()
  const { $checkoutService, $appInsights } = useNuxtApp()
  const featureStore = useFeaturesStore()
  const localeStore = useLocaleStore()
  const persistentStore = usePersistentStore()

  const userSelectedNoDemonstrator = ref(false)
  const paypalLoaded = ref(false)
  const paypalClientId = ref<string>()
  const paypalCountryCode = ref<string>()
  const paypalSdkNamespace = ref<string>(DEFAULT_PAYPAL_SDK_NAMESPACE)
  const shippingMethods = ref<ShippingMethod[]>([])
  // A shared cart clears its cookie in exitSharedCart() before the confirmation route
  // mounts, so the confirmation page cannot read the mode off the cart store when it
  // builds the purchase event. useCheckoutSubmit records it here, before that clear.
  const isSharedCartOrder = ref(false)
  // Set when the order POST succeeded but the confirmation GET after it failed, so
  // the confirmation page can retry the fetch instead of the shopper seeing an error
  // for an order that was placed. AB#64764
  const pendingConfirmation = ref<{ invoiceId: string, isSubscription: boolean }>()

  const resetInvoices = () => {
    currentCatalogMailingId.value = undefined
    invoiceMailing.value = undefined
    invoice.value = undefined
    invoiceSubscription.value = undefined
    confirmationInvoice.value = undefined
    pendingConfirmation.value = undefined
  }

  const fetchCatalogMailingInvoice = async (id: string) => {
    resetInvoices()
    currentCatalogMailingId.value = id
    const invoice = await $api()<Invoice>(`catalog-mailing/checkout/invoice/${id}`)
    invoiceMailing.value = { ...invoice, isCatalogMailing: true }
    return invoice
  }

  const isSubscriptionInvoice = (invoiceCheck: Invoice | InvoiceSubscription): invoiceCheck is InvoiceSubscription => 'firstKitShips' in invoiceCheck || 'firstKitShipsDate' in invoiceCheck

  const rootStore = useRootStore()
  const updateRecurlyPaymentMethod = async (payload: UpdateRecurlyPaymentMethodPayload) => {
    const recaptchaToken = rootStore.recaptchaToken
    const recaptchaAction = rootStore.recaptchaAction
    // token/action are single-use; clear before the call so a consumed token can't be re-sent
    rootStore.recaptchaToken = undefined
    rootStore.recaptchaAction = undefined
    const invoiceNew: Invoice | InvoiceSubscription = await $checkoutService.updateRecurlyPaymentMethod(payload, recaptchaToken ?? '', recaptchaAction)
    if (payload.catalogMailingId) {
      invoiceMailing.value = invoiceNew
    } else if (isSubscriptionInvoice(invoiceNew)) {
      invoiceSubscription.value = invoiceNew
    } else {
      invoice.value = invoiceNew
    }
    return invoiceNew
  }

  const removeDemonstrator = async () => {
    const invoiceNew = await $api()<Invoice>('checkout/invoice/demo', { method: 'DELETE' })
    invoice.value = invoiceNew
  }
  const removeSubscriptionDemonstrator = async () => {
    const invoiceNew = await $api()<InvoiceSubscription>('subscriptions/checkout/invoice/demo', { method: 'DELETE' })
    invoiceSubscription.value = invoiceNew
  }
  const submitMailingOrder = async (mailingId: string, payload: CheckoutProperties) => {
    let checkoutProperties: CheckoutProperties = { recurlyToken: payload?.recurlyToken }
    if (rootStore.signifydSessionId) {
      checkoutProperties = { ...checkoutProperties, signifydSessionId: rootStore.signifydSessionId, clientIpAddress: rootStore.clientIpAddress }
    }
    const invoice = await $api()<Invoice>(`catalog-mailing/checkout/invoice/${mailingId}`, { method: 'POST', body: { ...checkoutProperties } })
    return invoice
  }
  const updatePaymentMethod = async (payload: UpdatePaymentMethodPayload) => {
    const payloadCopy = { ...payload }
    // Auto-detect catalog mailing: only if we have BOTH stored ID AND active mailing invoice
    if (currentCatalogMailingId.value && invoiceMailing.value && !payload.catalogMailingId) {
      payloadCopy.catalogMailingId = currentCatalogMailingId.value
    }

    // Auto-detect subscription: if we have active subscription invoice
    if (invoiceSubscription.value && !payload.isSubscription) {
      payloadCopy.isSubscription = true
    }

    // Pass the appropriate invoice based on context
    let currentInvoice
    if (payloadCopy.isSubscription) {
      currentInvoice = invoiceSubscription.value
    } else if (payloadCopy.catalogMailingId) {
      currentInvoice = invoiceMailing.value
    } else {
      currentInvoice = invoice.value
    }
    const invoiceNew = await $checkoutService.updatePaymentMethod(payloadCopy, currentInvoice ?? null)
    if (payloadCopy.catalogMailingId) {
      invoiceMailing.value = invoiceNew
    } else if (isSubscriptionInvoice(invoiceNew)) {
      invoiceSubscription.value = invoiceNew
    } else {
      invoice.value = invoiceNew
    }
    return invoiceNew
  }
  const addSubscriptionLoyaltyPoints = async (points: string) => {
    const query = addCommonQueryParam()
    try {
      const url = '/subscriptions/loyalty-points'
      // The PATCH returns the subscription invoice; writing it to invoice.value put a
      // subscription payload in the regular cart invoice. AB#64790
      invoiceSubscription.value = await $api()<InvoiceSubscription>(url, { body: { points }, query, method: 'PATCH' })
    } finally {
      await fetchSubscriptionInvoice({ reset: false })
    }
  }
  const addSubscriptionCouponCodes = async (couponCodes: string[]) => {
    const query = addCommonQueryParam()
    try {
      const url = '/subscriptions/couponcodes'
      await $api()(url, { body: couponCodes, query, method: 'PUT' })
    } finally {
      await fetchSubscriptionInvoice({ reset: false })
    }
  }
  const removeSubscriptionCouponCode = async (couponCode: string) => {
    try {
      await $api()(`/subscriptions/couponcode/${couponCode}`, { method: 'DELETE' })
    } finally {
      // try/finally, matching addSubscriptionLoyaltyPoints and addSubscriptionCouponCodes
      // above: the invoice is always refreshed, but a failure still reaches the caller.
      // The previous catch-swallow also discarded fetchSubscriptionInvoice's own 5xx
      // re-throw, so a dead backend looked to the shopper like a successful coupon
      // removal. AB#63256
      await fetchSubscriptionInvoice({ reset: false })
    }
  }
  const fetchShippingMethods = async () => {
    const query = addCommonQueryParam()
    const shippingMethodsNew = await $api()<ShippingMethod[]>('shipping', { query })
    shippingMethods.value = shippingMethodsNew
  }
  // A failed invoice fetch means one of two very different things:
  //  - A 400 "validation" response: the body IS a usable invoice that also carries
  //    apiError (e.g. an invalid coupon), so it is recovered into the target ref and
  //    the errors render in place against the invoice.
  //  - Anything else -- a 5xx, a network failure, or a 4xx whose body is a plain error
  //    (401/403/404, a bad request): there is no invoice body. Assigning the error
  //    payload to the invoice ref corrupts checkout state and silently drops the
  //    user back to the shipping step. Those must be re-thrown so the caller
  //    (useAsyncData / useAction) can surface a real error. AB#60112
  //
  // An invoice body is told apart by what only an invoice carries: its own `apiError`
  // envelope or its `invoiceId`. The API's error bodies ({ httpStatusCode, errors },
  // problem details) have neither, so a status below 500 alone does not make one.
  const isInvoiceBody = (body: Record<string, unknown> | undefined): boolean =>
    body !== undefined && (asRecord(body.apiError) !== undefined || asString(body.invoiceId) !== undefined)
  const recoverInvoiceOrThrow = <T extends Invoice | InvoiceSubscription>(e: unknown, target: Ref<T | undefined>, context: string) => {
    const status = getErrorStatus(e)
    const envelope = getApiErrorData<T>(e)
    const isValidationEnvelope = (!status || status < 500) && isInvoiceBody(asRecord(envelope))
    if (!isValidationEnvelope) {
      $appInsights.trackException({ exception: toError(e), properties: { context } })
      throw e
    }
    $appInsights.trackTrace({ message: `fetch invoice threw error, but still got invoice ${JSON.stringify(envelope?.apiError)}` })
    target.value = envelope
  }
  // Regular checkout carried the same defect AB#60112 fixed for subscriptions: a 5xx or
  // network failure has no invoice body, and storing that payload as the invoice left
  // checkout holding a non-invoice object instead of reporting the failure. AB#63468
  const fetchInvoice = async () => {
    try {
      resetInvoices()
      invoice.value = await $checkoutService.fetchInvoice()
    } catch (e) {
      recoverInvoiceOrThrow(e, invoice, 'fetchInvoice')
    }
  }
  // `reset` clears all invoice state before fetching — needed on initial load,
  // but skipped for in-place refreshes (coupon/loyalty apply) so the checkout
  // body doesn't unmount and flash a blank page during the await. AB#60085
  const fetchSubscriptionInvoice = async ({ reset = true }: { reset?: boolean } = {}) => {
    if (reset) {
      resetInvoices()
    }
    const query = addCommonQueryParam()
    try {
      invoiceSubscription.value = await $api()<InvoiceSubscription>('subscriptions/checkout/invoice', { query })
    } catch (e) {
      recoverInvoiceOrThrow(e, invoiceSubscription, 'fetchSubscriptionInvoice')
    }
  }

  const updateCheckoutAddress = async (address: Address) => {
    const invoiceNew: Invoice = await $checkoutService.updateShippingAddress(address)
    invoice.value = invoiceNew
    return invoiceNew
  }

  const updateShippingMethod = async (shippingMethodId: number) => {
    const invoiceNew = await $checkoutService.updateShippingMethod(shippingMethodId)
    invoice.value = invoiceNew
  }
  const updateSubscriptionCheckoutAddress = async (address: Address) => {
    const query = addCommonQueryParam()
    const invoiceNew = await $api()<InvoiceSubscription>('subscriptions/checkout/invoice/address', { body: { ...address }, query, method: 'PATCH' })
    invoiceSubscription.value = invoiceNew
    return invoiceNew
  }

  // A saved Recurly PayPal billing info (paymentType 'PP') is paying with PayPal even though its
  // one-shot auth id is cleared — that's how primary (monthly) PayPal subs are stored so renewals
  // auto-charge. Direct-SDK PayPal (orders/prepaid/ideal/bancontact) always carries an auth id, so
  // the 'PP' fallback below only ever applies to the new subscription flow.
  const payingWithPaypal = (invoice?: Invoice) =>
    invoice?.paymentAccount?.paymentAuthorizationId != null || invoice?.paymentAccount?.paymentType === 'PP'
  const paypalFunding = (invoice?: Invoice) =>
    invoice?.paypalFunding ?? invoice?.paymentAccount?.paymentAuthorizationId ??
    (invoice?.paymentAccount?.paymentType === 'PP' ? PayPalFunding.PAYPAL : undefined)
  const invoiceShippingAddress = (invoice?: Invoice) => invoice?.shippingAddress
  const invoiceHash = (invoice?: Invoice) => invoice?.invoiceHash
  const invoiceErrors = (invoice?: Invoice) => invoice?.apiError?.errors ?? []
  const invoiceSelectedShippingMethod = (invoice?: Invoice) => shippingMethods.value.find(m => m.id === invoice?.savedShippingMethodId)
  const invoiceSelectedDemonstrator = (invoice?: Invoice) => invoice?.demoId
  const multipleShippingMethodsAvailable = computed(() => (shippingMethods.value.length ?? 0) > 1)
  const expeditedShippingMethodAvailable = computed(() => shippingMethods.value.some(m => m.isExpedited))
  const invoicePaymentAccount = (invoice?: Invoice) => invoice?.paymentAccount

  const catalogMailingPayingWithPaypal = computed(() => payingWithPaypal(invoiceMailing.value))
  const catalogMailingPayingFunding = computed(() => paypalFunding(invoiceMailing.value))
  const paypalShippingCurrency = computed(() =>
    localeStore.currencyForCountry(invoice.value?.shippingAddress?.country)
  )

  // Shared PayPal SDK configuration logic
  // countryCode is the country the SDK is being loaded FOR — the order's shipping
  // country on a cross-border order, which is not the storefront locale. Funding,
  // components and currency must all resolve from that one country, or the SDK is
  // loaded with (say) a DE Pay Later config against an AT merchant client ID.
  // Falls back to the locale when no country is known (e.g. preload before checkout).
  const getPaypalSdkConfig = (countryCode?: string) => {
    const enableFunding = [PayPalFunding.PAYPAL]
    const disableFunding: string[] = [PayPalFunding.SEPA, PayPalFunding.CARD]
    const components = ['buttons', 'marks', 'funding-eligibility']

    // Always include paylater in SDK for supported countries so marks work after login
    // The UI controls whether paylater is actually shown via paylaterEnabled
    const sdkCountryCode = (localeStore.country(countryCode)?.countryCode ?? localeStore.country().countryCode) as CountryCode
    const countrySupportsPayLater = PAYLATER_SUPPORTED_COUNTRIES.includes(sdkCountryCode)

    if (!countrySupportsPayLater) {
      disableFunding.push(PayPalFunding.CREDIT)
    }

    if (countrySupportsPayLater) {
      enableFunding.push(PayPalFunding.PAYLATER)
      components.push('messages')
    }
    if (featureStore.bancontactEnabled) {
      enableFunding.push(PayPalFunding.BANCONTACT)
    }
    if (featureStore.idealEnabled) {
      enableFunding.push(PayPalFunding.IDEAL)
    }
    if (featureStore.paypalCreditCardEnabled) {
      const idx = disableFunding.indexOf(PayPalFunding.CARD)
      if (idx !== -1) { disableFunding.splice(idx, 1) }
    }

    const hasPaymentFields = enableFunding.includes(PayPalFunding.IDEAL) || enableFunding.includes(PayPalFunding.BANCONTACT)
    if (hasPaymentFields) {
      components.push('payment-fields')
    }

    const intent = hasPaymentFields ? 'capture' : 'authorize'
    const buyerCountry = localeStore.env !== Env.Release ? sdkCountryCode : undefined
    const currency = countryCode ? localeStore.currencyForCountry(countryCode) : paypalShippingCurrency.value

    return { enableFunding, disableFunding, components, intent, buyerCountry, currency }
  }

  const paypalUrl = (clientId: string, countryCode?: string) => {
    const { enableFunding, disableFunding, components, intent, buyerCountry, currency } = getPaypalSdkConfig(countryCode)

    let options = `components=${components.join()}&enable-funding=${enableFunding.join()}&disable-funding=${disableFunding.join()}&currency=${currency}&intent=${intent}`

    if (buyerCountry) {
      options += `&buyer-country=${buyerCountry}`
    }

    return `https://www.paypal.com/sdk/js?client-id=${clientId}&${options}`
  }

  const getPaypalScriptOptions = (clientId: string, countryCode?: string): PayPalScriptOptions => {
    const { enableFunding, disableFunding, components, intent, buyerCountry, currency } = getPaypalSdkConfig(countryCode)

    const options: PayPalScriptOptions = {
      clientId,
      components: components.join(','),
      enableFunding: enableFunding.join(','),
      disableFunding: disableFunding.join(','),
      currency,
      intent
    }

    if (buyerCountry) {
      options.buyerCountry = buyerCountry
    }

    return options
  }

  // useCheckoutSubmit hands this the same checkout properties as submitOrder. The
  // recurlyToken is what carries an SCA-challenged card back to the backend after the
  // 3DS step, and Signifyd needs its session id on the order it is scoring.
  const submitSubscriptionOrder = async (checkoutProperties?: CheckoutProperties) => {
    const query = addCommonQueryParam()
    const body: Partial<CheckoutProperties> = {}
    if (checkoutProperties?.recurlyToken) {
      body.recurlyToken = checkoutProperties.recurlyToken
    }
    if (rootStore.signifydSessionId) {
      body.signifydSessionId = rootStore.signifydSessionId
      body.clientIpAddress = rootStore.clientIpAddress
    }
    const invoice = await $api()<Invoice>('subscriptions/checkout/invoice', { query, method: 'POST', body })
    if (!invoice.recurlyWants3DSecure) {
      await fetchConfirmationAfterOrder(invoice.invoiceId, true)
    }
    return invoice
  }

  const fetchConfirmationInvoice = async (invoiceId: string) => {
    confirmationInvoice.value = await $checkoutService.fetchConfirmationInvoice(invoiceId)
  }

  const fetchConfirmation = (invoiceId: string, isSubscription: boolean) =>
    isSubscription ? fetchSubscriptionConfirmationInvoice(invoiceId) : fetchConfirmationInvoice(invoiceId)

  // The order already exists once its POST returns, so a failed confirmation GET must
  // not fail the submit: record the invoice for the confirmation page to retry. AB#64764
  const fetchConfirmationAfterOrder = async (invoiceId: string, isSubscription = false) => {
    pendingConfirmation.value = undefined
    try {
      await fetchConfirmation(invoiceId, isSubscription)
    } catch (error) {
      $appInsights?.trackException({ exception: toError(error), properties: { context: 'order placed, confirmation fetch failed', invoiceId } })
      confirmationInvoice.value = undefined
      pendingConfirmation.value = { invoiceId, isSubscription }
    }
  }

  const CONFIRMATION_RETRY_DELAYS_MS = [1000, 2000, 4000]

  const retryConfirmationFetch = async () => {
    const pending = pendingConfirmation.value
    if (!pending) {
      return undefined
    }
    // Kept until a retry loads the invoice so a remount retries again. It is in-memory
    // only, so a hard reload of the confirmation page still redirects home.
    for (const delay of CONFIRMATION_RETRY_DELAYS_MS) {
      await new Promise(resolve => setTimeout(resolve, delay))
      try {
        await fetchConfirmation(pending.invoiceId, pending.isSubscription)
        // An empty 200 is not a loaded confirmation: keep it pending and retry.
        if (confirmationInvoice.value) {
          pendingConfirmation.value = undefined
          return confirmationInvoice.value
        }
      } catch (error) {
        $appInsights?.trackException({ exception: toError(error), properties: { context: 'confirmation fetch retry failed', invoiceId: pending.invoiceId } })
      }
    }
    return undefined
  }

  let confirmationRetry: Promise<InvoiceConfirmation | undefined> | undefined

  // Called by the confirmation page; resolves to the confirmation invoice, or undefined
  // if every retry failed or no fetch is pending. A page that remounts while a retry is
  // still running joins it rather than starting a second loop for the same invoice.
  const retryPendingConfirmation = () => {
    confirmationRetry ??= retryConfirmationFetch().finally(() => {
      confirmationRetry = undefined
    })
    return confirmationRetry
  }

  const fetchConfirmationPdf = async (confirmationId: string, invoiceId: string, email: string) => {
    const content = await $api()<string>(`checkout/invoice/confirmation/pdf/${invoiceId}?email=${encodeURIComponent(email)}`, { responseType: 'blob' })
    const fileSaver = await import('file-saver')
    fileSaver.saveAs(content, `${confirmationId}.pdf`)
  }

  const fetchPaypalOrderStatus = async (orderId: string) => {
    return await $api()<{ transStatus?: string, captureId?: string, orderId?: string, authorizationId?: string }>(`paypal/order/${orderId}`)
  }

  // Server-side authorization for an order the buyer approved but that came back with
  // no authorization id — tried before the refund fallback in usePaypal.
  const authorizePaypalOrder = async (orderId: string) => {
    return await $api()<{ authorizationId?: string, captureId?: string, orderId?: string }>(`paypal/order/authorize/${orderId}`, { method: 'POST' })
  }

  const fetchSubscriptionConfirmationInvoice = async (invoiceId: string) => {
    confirmationInvoice.value = await $api()<InvoiceConfirmation>(`subscriptions/checkout/invoice/confirmation/${invoiceId}`)
    return invoice
  }
  const updateDemonstrator = async (demoId: number) => {
    const query = addCommonQueryParam()
    const invoiceNew = await $api()<Invoice>(
        `checkout/invoice/demo?demoId=${demoId}`, { method: 'PATCH', query })
    invoice.value = invoiceNew
  }
  const updateSubscriptionDemonstrator = async (demoId: number) => {
    const query = addCommonQueryParam()
    const invoiceNew = await $api()<InvoiceSubscription>(
        `subscriptions/checkout/invoice/demo?demoId=${demoId}`, { method: 'PATCH', query })
    invoiceSubscription.value = invoiceNew
  }

  const submitOrder = async (checkoutProperties?: CheckoutProperties) => {
    const utm = persistentStore.getPersistentValue('utm')
    if (utm) {
      checkoutProperties = { ...checkoutProperties, utmSource: utm.utmSource, utmCampaign: utm.utmCampaign, utmMedium: utm.utmMedium }
    }
    if (rootStore.signifydSessionId) {
      checkoutProperties = { ...checkoutProperties, signifydSessionId: rootStore.signifydSessionId, clientIpAddress: rootStore.clientIpAddress }
    }
    const response = await $checkoutService.submit(checkoutProperties)
    if (!response.recurlyWants3DSecure) {
      isSharedCartOrder.value = useCartStore().isSharedCartMode
      invoice.value = undefined
    }
    return response
  }

  // Method to reset the checkout state.
  // This is used so that checkout state doesn't impact RecurlyPaymentMethods.vue component when it is used outside of the checkut flow.
  const clearStoreState = () => {
    invoiceMailing.value = undefined
    invoice.value = undefined
    confirmationInvoice.value = undefined
    invoiceSubscription.value = undefined
    userSelectedNoDemonstrator.value = false
    paypalLoaded.value = false
    paypalClientId.value = undefined
    paypalCountryCode.value = undefined
    paypalSdkNamespace.value = DEFAULT_PAYPAL_SDK_NAMESPACE
    shippingMethods.value = []
    isSharedCartOrder.value = false
    pendingConfirmation.value = undefined
  }

  return {
    fetchCatalogMailingInvoice,
    catalogMailingPayingWithPaypal,
    catalogMailingPayingFunding,
    multipleShippingMethodsAvailable,
    expeditedShippingMethodAvailable,
    userSelectedNoDemonstrator,
    invoiceShippingAddress,
    shippingMethods,
    updateShippingMethod,
    updateDemonstrator,
    updateSubscriptionDemonstrator,
    invoiceSelectedDemonstrator,
    invoiceSelectedShippingMethod,
    invoicePaymentAccount,
    updateRecurlyPaymentMethod,
    updateCheckoutAddress,
    submitOrder,
    submitSubscriptionOrder,
    invoiceHash,
    updateSubscriptionCheckoutAddress,
    removeSubscriptionDemonstrator,
    removeDemonstrator,
    fetchSubscriptionInvoice,
    paypalUrl,
    getPaypalScriptOptions,
    fetchInvoice,
    fetchShippingMethods,
    submitMailingOrder,
    updatePaymentMethod,
    payingWithPaypal,
    paypalFunding,
    invoiceErrors,
    paypalLoaded,
    paypalClientId,
    paypalCountryCode,
    paypalSdkNamespace,
    invoiceMailing,
    isSubscriptionInvoice,
    addSubscriptionLoyaltyPoints,
    addSubscriptionCouponCodes,
    removeSubscriptionCouponCode,
    confirmationInvoice,
    fetchConfirmationInvoice,
    fetchConfirmationAfterOrder,
    retryPendingConfirmation,
    pendingConfirmation,
    fetchConfirmationPdf,
    fetchPaypalOrderStatus,
    authorizePaypalOrder,
    fetchSubscriptionConfirmationInvoice,
    invoiceSubscription,
    invoice,
    isSharedCartOrder,
    clearStoreState,
    paypalShippingCurrency
  }
})
